import React, { useState, useMemo } from 'react';
import { Grid, styled, Typography, Box } from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { useIntl } from 'react-intl';

import { SdmRichText, SdmStack } from '../';
import translations from 'utils/translations';

import { Container } from './Contents';

const TABS = {
  overview: 0,
  details: 1,
};

export const Image = styled('img')({
  width: '100%',
});

const Link = styled(({ url, text, className }) => {
  return (
    <StyledTypography
      className={className}
      onClick={() => window.open(url, '_blank')}
    >
      {text}
      <ArrowForward />
    </StyledTypography>
  );
})({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const StyledTypography = styled(Typography)(({ theme, fontWeight = 400 }) => ({
  fontSize: 16,
  lineHeight: '24px',
  color: theme.palette.grey[900],
  fontWeight: fontWeight,
}));

const TechSpecItem = ({ title, content }) => {
  return (
    <Box sx={{ marginBottom: '5px' }}>
      {title && <StyledTypography fontWeight={600}>{title}</StyledTypography>}
      <StyledTypography fontWeight={400}>{content}</StyledTypography>
    </Box>
  );
};

const TechSpecList = ({ items }) => {
  return (
    <StyledStack>
      {items.map((el) => (
        <TechSpecItem key={el.title} {...el} />
      ))}
    </StyledStack>
  );
};

const useTechSpecs = ({
  productName,
  year,
  designer,
  materials,
  features,
  productionProcess,
  dimensions,
  officialWebsiteLink,
  productSheetLink,
  collectionLink,
}) => {
  const intl = useIntl();
  const i18n = translations.dataSheet(intl);

  return useMemo(() => {
    const techSpecs = [];

    productName &&
      techSpecs.push({
        title: i18n.productName,
        content: <StyledTypography>{productName}</StyledTypography>,
      });
    designer &&
      techSpecs.push({
        title: i18n.designer,
        content: <StyledTypography>{designer}</StyledTypography>,
      });
    materials &&
      techSpecs.push({
        title: i18n.materials,
        content: <StyledTypography>{materials.join(', ')}</StyledTypography>,
      });
    features &&
      techSpecs.push({
        title: i18n.features,
        content: <StyledTypography>{features.join(', ')}</StyledTypography>,
      });
    dimensions &&
      techSpecs.push({
        title: i18n.dimensions,
        content: <RichText text={dimensions} />,
      });
    productionProcess &&
      techSpecs.push({
        title: i18n.productionProcess,
        content: <RichText text={productionProcess} />,
      });
    year &&
      techSpecs.push({
        title: i18n.year,
        content: <StyledTypography>{year}</StyledTypography>,
      });
    productSheetLink?.url &&
      techSpecs.push({
        title: i18n.productSheetLink,
        content: <Link {...productSheetLink} />,
      });
    collectionLink?.url &&
      techSpecs.push({
        title: i18n.collectionLink,
        content: <Link {...collectionLink} />,
      });
    officialWebsiteLink?.url &&
      techSpecs.push({
        title: i18n.officialWebsiteLink,
        content: <Link {...officialWebsiteLink} />,
      });

    return techSpecs;
  }, [
    productName,
    year,
    designer,
    materials,
    features,
    productionProcess,
    dimensions,
    officialWebsiteLink,
    productSheetLink,
    collectionLink,
    i18n,
  ]);
};

const RichText = ({ text }) => {
  return (
    <SdmRichText
      variant="transparent"
      editorStyleOverride={{
        maxHeight: 'none',
        marginRight: 0,
        paddingLeft: 0,
        minHeight: 'none',
      }}
      formControlStyle={{
        margin: 0,
        marginTop: 0,
      }}
      labelStyle={{
        display: 'none',
      }}
      value={text}
      readOnly
    />
  );
};

const StyledStack = styled(SdmStack)({
  gap: 10,
});

const StyledTabsBox = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
}));

const StyledTabButton = styled(Box)(({ theme, active }) => ({
  flex: 1,
  textAlign: 'center',
  padding: '5px 0',
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: active ? 600 : 400,
  color: theme.palette.grey[900],
  borderBottom: `2px solid ${
    active ? theme.palette.primary.main : theme.palette.grey[300]
  }`,
  cursor: 'pointer',
}));

const StyledContainer = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  padding: '10px',
  marginTop: '20px',
  marginBottom: '20px',
}));

export const DataSheet = ({
  src,
  alt,
  overview,
  productName,
  year,
  designer,
  materials,
  features,
  productionProcess,
  dimensions,
  officialWebsiteLink,
  productSheetLink,
  collectionLink,
}) => {
  const techSpecs = useTechSpecs({
    productName,
    year,
    designer,
    materials,
    features,
    productionProcess,
    dimensions,
    officialWebsiteLink,
    productSheetLink,
    collectionLink,
  });
  const [activeTab, setActiveTab] = useState(TABS.overview);

  return (
    <Container yGutters>
      <StyledContainer container spacing={3}>
        <Grid item md={6} xs={12}>
          <Image src={src} alt={alt} />
        </Grid>
        <Grid item md={6} xs={12}>
          <StyledTabsBox>
            <StyledTabButton
              active={activeTab === TABS.overview}
              onClick={() => setActiveTab(TABS.overview)}
            >
              Descrizione del prodotto
            </StyledTabButton>
            <StyledTabButton
              active={activeTab === TABS.details}
              onClick={() => setActiveTab(TABS.details)}
            >
              Caratteristiche del prodotto
            </StyledTabButton>
          </StyledTabsBox>

          <Box mt={3}>
            <TabPanel value={activeTab} index={0}>
              <TechSpecItem content={<RichText text={overview} />} />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <TechSpecList items={techSpecs} />
            </TabPanel>
          </Box>
        </Grid>
      </StyledContainer>
    </Container>
  );
};
